<template>
  <form @submit.prevent="handleSubmit">
    <form-divider>
      <input-field
        v-model="formData.name"
        :errorMessage="v$?.formData['name']?.$errors[0]?.$message"
        :isError="v$?.formData['name']?.$error"
        :required="true"
        label="Name"
      />
      <input-field
        v-if="integrationTypeId !== 1"
        v-model="formData.config.endpoint"
        :errorMessage="v$?.formData?.config['endpoint']?.$errors[0]?.$message"
        :isError="v$?.formData?.config['endpoint']?.$error"
        :required="true"
        label="Endpoint"
      />
      <multi-json-input @input="handleTopicChange" :topics="formData.config.topicToChannel" />
      <tags-input
        :label="$t('Integrations.Channels')"
        :tags="formData.config.channels"
        @on-tag-mutate="(tags) => (formData.config.channels = tags)"
      />
      <input-field
        v-model="formData.executionOrder"
        :label="$t('Integrations.ExecutionOrder')"
        type="number"
        :errorMessage="v$?.formData['executionOrder']?.$errors[0]?.$message"
        :isError="v$?.formData['executionOrder']?.$error"
      />
      <checkbox-input v-model="formData.isActive" :is-checked="formData.isActive" label="Active" />
    </form-divider>
    <button-wrapper :is-submit-loading="isSubmitLoading" />
  </form>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import InputField from "../../../core/Components/FormElements/InputField.vue";
import VSelect from "vue-select";
import TheButton from "../../../core/Components/TheButton.vue";
import IntegrationService, { CreateEditIntegration } from "../Services/IntegrationService";
import CheckboxInput from "@/core/Components/FormElements/CheckboxInput.vue";
import checkIfObjectEmpty, { CheckObjectsForPatch, ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import { maxLength, required, requiredIf, minValue } from "@vuelidate/validators";
import useValidate from "@vuelidate/core";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import { useToast } from "vue-toastification";
import TagsInput from "@/core/Components/FormElements/TagsInput.vue";
import { specialCharactersValidator } from "@/core/Services/CustomValidators";
import MultiJsonInput from "@/core/Components/FormElements/MultiJsonInput.vue";

export default defineComponent({
  components: {
    TagsInput,
    InputField,
    VSelect,
    TheButton,
    CheckboxInput,
    FormDivider,
    ButtonWrapper,
    MultiJsonInput,
  },
  props: ["integrationTypeId", "applicationId", "data", "refreshData"],
  data() {
    return {
      v$: useValidate() as any,
      validationErrors: [] as any,
      integrationService: new IntegrationService(),
      isSubmitLoading: false as boolean,
      finalForm: [] as { op: string; path: string; value: any }[],
      toast: useToast(),
      formData: {
        id: this.data.id,
        name: this.data.name,
        integrationTypeId: this.data.integrationTypeId,
        config: {
          endpoint: this.data?.config?.endpoint,
          topicToChannel: this.data?.config?.topicToChannel,
          channels: this.data?.config?.channels,
        },
        applicationId: this.applicationId,
        isActive: this.data.isActive,
        executionOrder: this.data.executionOrder,
      } as CreateEditIntegration,
      integrationFromAPI: {},
    };
  },

  validations() {
    return {
      formData: {
        name: {
          required,
          maxLength: maxLength(50),
          specialCharacters: specialCharactersValidator,
        },
        executionOrder: {
          minValue: minValue(1),
        },
        config: {
          endpoint: {
            required: requiredIf(() => this.integrationTypeId !== 1),
          },
        },
      },
    };
  },
  created() {
    this.integrationFromAPI = Object.assign({}, this.formData);
  },

  watch: {
    applicationId(value) {
      this.formData.applicationId = value;
    },
  },

  methods: {
    async handleSubmit() {
      this.v$.$validate();
      this.validationErrors = this.v$.$errors;
      if (!this.v$.$error) {
        this.isSubmitLoading = true;
        try {
          if (this.formData.config.channels) {
            this.formData.config.channels = this.formData.config.channels.map((channel: string) => {
              if (typeof channel === "string") {
                return Number(channel.trim());
              }

              return channel;
            });
          }

          if (this.integrationTypeId === 1) {
            this.formData.config.endpoint = null;
          }

          if (this.formData.config.channels && this.formData.config.channels.length === 0) {
            this.formData.config.channels = null;
          }

          if (this.formData.executionOrder != null) {
            this.formData.executionOrder = Number(this.formData.executionOrder);
          }

          if (this.formData.executionOrder == 0) {
            this.formData.executionOrder = null;
          }

          if (checkIfObjectEmpty(this.formData.config.topicToChannel)) {
            this.formData.config.topicToChannel = null;
          }

          const integrationPatch = await CheckObjectsForPatch({
            initialObject: this.integrationFromAPI,
            editedObject: this.formData,
          });

          await this.integrationService.editIntegration({
            integrationPatch,
            id: this.data.id,
          });
          this.toast.success((this as any).$t("General.EditedSuccessfully"));
          await this.refreshData();
        } catch (error: any) {
          this.toast.error(ErrorMessageFormatter(error));
        } finally {
          this.isSubmitLoading = false;
        }
      }
    },
    handleTopicChange(value: Record<string, number>) {
      this.formData.config.topicToChannel = value;
    },
  },
});
</script>
<style scoped></style>
